import { SxProps, TextField, Theme } from "@mui/material";
import * as React from "react";

const TextArea = React.forwardRef(function TextArea(
  props: {
    onChange: (v: string) => void;
    validateInput?: (input: string) => boolean;
    errorFeedbackText?: string;
    inputPattern?: string;
    value?: string;
    label?: string;
    required?: boolean;
    minRows?: number;
    inputLabelShrink?: boolean;
    id?: string;
    sx?: SxProps<Theme>;
  },
  ref: React.Ref<HTMLInputElement | undefined>
) {
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(`${props.label} cannot be left blank`);
  const [valid, setValid] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  const linksPattern = "^((?!http|www|mailto).)*$";

  const validate = (value: string) => {
    if ((props.required ?? true) && value.trim() === "") {
      setValid(false);
      setErrorMessage(`${props.label} cannot be left blank`);
    } else if (!new RegExp(linksPattern).test(value)) {
      setValid(false);
      setErrorMessage(`${props.label} cannot contain links`);
    } else if (props.validateInput && !props.validateInput(value)) {
      setValid(false);
      setErrorMessage(props.errorFeedbackText ?? undefined);
    } else {
      setValid(true);
      setErrorMessage(undefined);
    }
  };

  const handleFormInteration = (e: React.FormEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    validate(e.currentTarget.value);
    setDirty(true);
  };

  const handleOnValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Dirty false so error disappears while user is typing
    setDirty(false);
    props.onChange(e.currentTarget.value);
  };

  const isError = !valid && dirty;

  return (
    <TextField
      id={props.id}
      inputRef={ref}
      value={props.value}
      variant="outlined"
      error={isError}
      helperText={isError && errorMessage}
      label={props.label}
      size="medium"
      onChange={handleOnValueChange}
      onBlur={handleFormInteration}
      sx={{ marginBottom: "1.5rem", ...props.sx }}
      fullWidth
      aria-invalid={!valid}
      inputProps={{
        pattern: props.inputPattern,
        onInvalid: (e) => {
          e.preventDefault();
          handleFormInteration(e);
        },
      }}
      InputLabelProps={{ shrink: props.inputLabelShrink }}
      required={props.required ?? true}
      multiline={props.minRows !== undefined}
      minRows={props.minRows}
    />
  );
});

export { TextArea };
